import React, { useState, useEffect } from 'react';
import { getGlobalLeaderboard, getHallOfFame } from '../DatabaseAPI';
import { Button } from '../components/ui';
import './LeaderboardPage.css';

function LeaderboardPage({ user, setPage, leagueId }) {
    const [filterType, setFilterType] = useState('highscore');
    const [timeframe, setTimeframe] = useState('lastweek');
    const [leaderboardData, setLeaderboardData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Hall of Fame state
    const [viewMode, setViewMode] = useState('leaderboard'); // 'leaderboard' or 'halloffame'
    const [hallOfFameData, setHallOfFameData] = useState([]);
    const [hofLoading, setHofLoading] = useState(false);
    const [hofError, setHofError] = useState(null);
    
    useEffect(() => {
        console.log('LeaderboardPage: Fetching leaderboard data with filter:', { filterType, timeframe });
        setLoading(true);
        setError(null);
        
        getGlobalLeaderboard(filterType, timeframe)
            .then(data => {
                console.log('LeaderboardPage: Received leaderboard data:', data);
                if (data && data.leaderboard && Array.isArray(data.leaderboard)) {
                    setLeaderboardData(data.leaderboard);
                } else {
                    setError('Invalid leaderboard data received');
                }
                setLoading(false);
            })
            .catch(error => {
                console.error('LeaderboardPage: Error loading leaderboard:', error);
                setError('Failed to load leaderboard');
                setLoading(false);
            });
    }, [filterType, timeframe]);

    const handleNavigation = (destination) => {
        console.log('LeaderboardPage navigation:', destination);
        
        switch(destination) {
            case 'LEAGUES':
                setPage('HOME');
                break;
            case 'TEAM':
                setPage('TEAM');
                break;
            case 'LEAGUE':
                setPage('LEAGUE');
                break;
            case 'GEAR':
                setPage('GEAR');
                break;
            case 'LEADERBOARD':
                // Already on leaderboard
                break;
            default:
                console.log('Unknown navigation destination:', destination);
                break;
        }
    };

    const getRankSuffix = (rank) => {
        if (rank === 1) return 'ST';
        if (rank === 2) return 'ND';
        if (rank === 3) return 'RD';
        return 'TH';
    };

    const getScore = (userData) => {
        if (filterType === 'highscore') {
            return timeframe === 'lifetime' ? userData.highscore_lifetime : userData.highscore_lastweek;
        } else {
            return timeframe === 'lifetime' ? userData.mythril_lifetime : userData.mythril_lastweek;
        }
    };

    const handleUserClick = (userData) => {
        // Users are not selectable yet, but we'll log for future implementation
        console.log('User clicked:', userData.username);
    };

    const handleFilterChange = (newFilterType, newTimeframe) => {
        console.log('LeaderboardPage: Filter changed to:', { newFilterType, newTimeframe });
        setFilterType(newFilterType);
        setTimeframe(newTimeframe);
    };

    const handleHallOfFameToggle = () => {
        if (viewMode === 'halloffame') {
            // Switch back to leaderboard
            setViewMode('leaderboard');
        } else {
            // Switch to Hall of Fame
            setViewMode('halloffame');
            // Load Hall of Fame data if not already loaded
            if (hallOfFameData.length === 0 && !hofError) {
                loadHallOfFame();
            }
        }
    };

    const loadHallOfFame = () => {
        console.log('🏆 LeaderboardPage: Loading Hall of Fame data');
        setHofLoading(true);
        setHofError(null);
        
        getHallOfFame()
            .then(data => {
                console.log('🏆 LeaderboardPage: Received Hall of Fame data:', data);
                if (data && data.hall_of_fame && Array.isArray(data.hall_of_fame)) {
                    setHallOfFameData(data.hall_of_fame);
                } else {
                    setHofError('Invalid Hall of Fame data received');
                }
                setHofLoading(false);
            })
            .catch(error => {
                console.error('🏆 LeaderboardPage: Error loading Hall of Fame:', error);
                setHofError('Failed to load Hall of Fame');
                setHofLoading(false);
            });
    };

    if (loading) {
        return (
            <div className="leaderboard-page-loading">
                <h2>Loading Leaderboard...</h2>
            </div>
        );
    }

    if (error) {
        return (
            <div className="leaderboard-page-error">
                <h2>Error Loading Leaderboard</h2>
                <p>{error}</p>
                <Button onClick={() => window.location.reload()} variant="secondary">Retry</Button>
            </div>
        );
    }

    // Data is already sorted by the backend
    const sortedUsers = leaderboardData;
    
    // Render Hall of Fame view
    if (viewMode === 'halloffame') {
        if (hofLoading) {
            return (
                <div className="leaderboard-page hof-mode">
                    <div className="leaderboard-header">
                        <Button
                            className="leaderboard-leagues-button"
                            onClick={() => handleNavigation('LEAGUES')}
                            variant="secondary"
                        >
                            LEAGUES
                        </Button>
                        <Button
                            className="leaderboard-hof-button active"
                            onClick={handleHallOfFameToggle}
                            variant="secondary"
                        >
                            HALL OF FAME
                        </Button>
                    </div>
                    <div className="leaderboard-page-loading">
                        <h2>Loading Hall of Fame...</h2>
                    </div>
                </div>
            );
        }

        if (hofError) {
            return (
                <div className="leaderboard-page hof-mode">
                    <div className="leaderboard-header">
                        <Button
                            className="leaderboard-leagues-button"
                            onClick={() => handleNavigation('LEAGUES')}
                            variant="secondary"
                        >
                            LEAGUES
                        </Button>
                        <Button
                            className="leaderboard-hof-button active"
                            onClick={handleHallOfFameToggle}
                            variant="secondary"
                        >
                            HALL OF FAME
                        </Button>
                    </div>
                    <div className="leaderboard-page-error">
                        <h2>Error Loading Hall of Fame</h2>
                        <p>{hofError}</p>
                        <Button onClick={loadHallOfFame} variant="secondary">Retry</Button>
                    </div>
                </div>
            );
        }

        return (
            <div className="leaderboard-page hof-mode">
                <div className="leaderboard-header">
                    <Button
                        className="leaderboard-leagues-button"
                        onClick={() => handleNavigation('LEAGUES')}
                        variant="secondary"
                    >
                        LEAGUES
                    </Button>
                    <Button
                        className="leaderboard-hof-button active"
                        onClick={handleHallOfFameToggle}
                        variant="secondary"
                    >
                        HALL OF FAME
                    </Button>
                </div>

                <div className="leaderboard-list-container">
                    {hallOfFameData.length === 0 ? (
                        <div className="leaderboard-empty">
                            <p>No Hall of Fame performances yet. Be the first!</p>
                        </div>
                    ) : (
                        hallOfFameData.map((performance) => (
                            <div key={performance.rank} className="hof-card">
                                {/* Trophy Icon - Top Left */}
                                <div className="hof-trophy">
                                    {performance.rank === 1 && '🏆'}
                                    {performance.rank === 2 && '🥈'}
                                    {performance.rank === 3 && '🥉'}
                                    {performance.rank > 3 && `#${performance.rank}`}
                                </div>
                                
                                {/* Player Info - Main Content */}
                                <div className="hof-player-info">
                                    <div className="hof-player-name">{performance.player_name}</div>
                                    <div className="hof-position-badge">{performance.position}</div>
                                    <div className="hof-context">
                                        Week {performance.week}, {performance.year} • {performance.team_name}
                                    </div>
                                    <div className="hof-stats">{performance.stat_string}</div>
                                </div>
                                
                                {/* Score - Right Side */}
                                <div className="hof-score">
                                    <div className="hof-score-value">{performance.score.toFixed(1)}</div>
                                    <div className="hof-score-label">POINTS</div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        );
    }
    
    // Render normal leaderboard view
    return (
        <div className="leaderboard-page">
            {/* Fixed Header with LEAGUES and HALL OF FAME buttons */}
            <div className="leaderboard-header">
                <Button
                    className="leaderboard-leagues-button"
                    onClick={() => handleNavigation('LEAGUES')}
                    variant="secondary"
                >
                    LEAGUES
                </Button>
                <Button
                    className="leaderboard-hof-button"
                    onClick={handleHallOfFameToggle}
                    variant="secondary"
                >
                    HALL OF FAME
                </Button>
            </div>

            {/* Scrollable User List */}
            <div className="leaderboard-list-container">
                {sortedUsers.length === 0 ? (
                    <div className="leaderboard-empty">
                        <p>No users found on the leaderboard yet.</p>
                    </div>
                ) : (
                    sortedUsers.map((userData, index) => {
                        const rank = index + 1;
                        // PR1: leaderboard rows are keyed on public_id (UUID), not the
                        // removed raw integer id. Self-highlight compares the current
                        // authenticated user's public_id (passed in as `user.public_id`
                        // by AppRouter.js, sourced from useAuth()/authenticatedUser —
                        // NOT the sessionStorage team id used elsewhere in this app)
                        // against each row's public_id. `user.public_id` is guaranteed
                        // present here: AppRouter.js throws loudly before rendering this
                        // page if the authenticated user is missing public_id, rather
                        // than silently defaulting this comparison to false forever.
                        const isCurrentUser = user && user.public_id && userData.public_id === user.public_id;
                        const score = getScore(userData);
                    
                        return (
                            <div
                                key={userData.public_id}
                                className={`leaderboard-card ${isCurrentUser ? 'current-user' : ''}`}
                                onClick={() => handleUserClick(userData)}
                            >
                                {/* Rank Badge - Top Left */}
                                <div className="rank-badge">
                                    <span className="rank-number">{rank}</span>
                                    <span className="rank-suffix">{getRankSuffix(rank)}</span>
                                </div>
                                
                                {/* Player Helmets - Left Side */}
                                <div className="player-helmets">
                                    {userData.teams && userData.teams.length > 0 ? (
                                        userData.teams.slice(0, 3).map((teamName, idx) => (
                                            <img key={idx} src="/art/TeamIcon.png" alt="helmet" className="helmet-icon" />
                                        ))
                                    ) : (
                                        <>
                                            <img src="/art/TeamIcon.png" alt="helmet" className="helmet-icon" />
                                            <img src="/art/TeamIcon.png" alt="helmet" className="helmet-icon" />
                                            <img src="/art/TeamIcon.png" alt="helmet" className="helmet-icon" />
                                        </>
                                    )}
                                </div>
                                
                                {/* User Info - Right Side */}
                                <div className="user-info">
                                    <div className="username">{userData.username}</div>
                                    <div className="score-label">TOTAL SCORE</div>
                                    <div className="score-value">
                                        {filterType === 'highscore'
                                            ? score.toFixed(1) + 'PT'
                                            : score.toFixed(0)
                                        }
                                    </div>
                                </div>
                                
                                {/* Medal Icon - Top Right (for top 3) */}
                                {rank <= 3 && (
                                    <div className="medal-icon">
                                        {rank === 1 && '🥇'}
                                        {rank === 2 && '🥈'}
                                        {rank === 3 && '🥉'}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Fixed Footer with Filter Controls */}
            <div className="leaderboard-footer">
                {/* Row 1: High Score */}
                <div className="filter-row">
                    <div className="filter-label">HIGH SCORE</div>
                    <div className="filter-buttons">
                        <Button
                            className={filterType === 'highscore' && timeframe === 'lifetime' ? 'active' : ''}
                            onClick={() => handleFilterChange('highscore', 'lifetime')}
                            variant="secondary"
                        >
                            LIFETIME
                        </Button>
                        <Button
                            className={filterType === 'highscore' && timeframe === 'lastweek' ? 'active' : ''}
                            onClick={() => handleFilterChange('highscore', 'lastweek')}
                            variant="secondary"
                        >
                            LAST WEEK
                        </Button>
                    </div>
                </div>
                
                {/* Row 2: Mythril */}
                <div className="filter-row">
                    <div className="filter-label">MYTHRIL</div>
                    <div className="filter-buttons">
                        <Button
                            className={filterType === 'mythril' && timeframe === 'lifetime' ? 'active' : ''}
                            onClick={() => handleFilterChange('mythril', 'lifetime')}
                            variant="secondary"
                        >
                            LIFETIME
                        </Button>
                        <Button
                            className={filterType === 'mythril' && timeframe === 'lastweek' ? 'active' : ''}
                            onClick={() => handleFilterChange('mythril', 'lastweek')}
                            variant="secondary"
                        >
                            LAST WEEK
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LeaderboardPage;